import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useAvailableTenantMetadataContext } from "contexts/availableTenantMetadata";
import {
  DateRangeProvider,
  useDateRange,
  type DateRangeConfig,
} from "contexts/DateRangeContext";
import { DateRangeSelector } from "components/common/DateRangeSelector";
import { MultiSelectAutocomplete } from "components/common/MultiSelectAutocomplete";
import { sorted } from "helpers";
import { DEFAULT_DESELECTED_TENANT_NAMES } from "hooks/useScrapEyeTenantSelection";
import { useCallback, useMemo, useState } from "react";
import { CombinedReviewerActionsChart } from "./CombinedReviewerActionsChart";
import { TenantReviewerCard } from "./TenantReviewerCard";

const REVIEWER_TRACKING_DATE_RANGE_CONFIG: DateRangeConfig = {
  paramPrefix: "rt",
  defaultStartDate: dayjs().subtract(30, "day"),
  defaultEndDate: dayjs(),
};

type TenantOption = Readonly<{
  id: string;
  longName: string;
}>;

const ReviewerTrackingOverviewContent = () => {
  const { t } = useTenantTranslation();
  const { tenantMetadataList, isLoading } = useAvailableTenantMetadataContext();
  const { startDate, endDate } = useDateRange();

  const scrapEyeTenants = useMemo(
    () =>
      sorted(
        tenantMetadataList.filter((tenant) =>
          tenant.feature_flags.includes("scrap_eyes")
        ),
        (tenant) => tenant.tenant_name
      ),
    [tenantMetadataList]
  );

  const tenantOptions: readonly TenantOption[] = useMemo(
    () =>
      scrapEyeTenants.map((tenant) => ({
        id: tenant.tenant_name,
        longName: tenant.long_name,
      })),
    [scrapEyeTenants]
  );

  const defaultSelectedTenantIds = useMemo(
    () =>
      scrapEyeTenants
        .filter(
          (tenant) =>
            !DEFAULT_DESELECTED_TENANT_NAMES.includes(tenant.tenant_name)
        )
        .map((tenant) => tenant.tenant_name),
    [scrapEyeTenants]
  );

  const [selectedTenantIds, setSelectedTenantIds] = useState<
    readonly string[] | null
  >(null);

  const activeTenantIds = selectedTenantIds ?? defaultSelectedTenantIds;

  const handleTenantSelectionChange = useCallback((ids: readonly string[]) => {
    setSelectedTenantIds(ids);
  }, []);

  const filterTenantOption = useCallback(
    (option: TenantOption, input: string) => {
      const query = input.toLowerCase();
      return (
        option.id.toLowerCase().includes(query) ||
        option.longName.toLowerCase().includes(query)
      );
    },
    []
  );

  const activeTenantDescriptors = useMemo(() => {
    const activeSet = new Set(activeTenantIds);
    return scrapEyeTenants
      .filter((tenant) => activeSet.has(tenant.tenant_name))
      .map((tenant) => ({
        tenantName: tenant.tenant_name,
        tenantLongName: tenant.long_name,
      }));
  }, [scrapEyeTenants, activeTenantIds]);

  const start = startDate.format("YYYY-MM-DD");
  const end = endDate.format("YYYY-MM-DD");

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3} sx={{ p: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="h4" sx={{ flex: 1 }}>
          {t("reviewData")}
        </Typography>
        <DateRangeSelector />
      </Stack>

      <MultiSelectAutocomplete
        options={tenantOptions}
        selectedIds={activeTenantIds}
        onChange={handleTenantSelectionChange}
        getOptionLabel={(option) => option.longName}
        filterOption={filterTenantOption}
        label={t("tenants")}
        chipPlacement="below"
      />

      {activeTenantDescriptors.length > 0 && (
        <Stack spacing={2}>
          <Box
            sx={{
              p: 2,
              backgroundColor: "grey.50",
              borderRadius: 2,
              border: 1,
              borderColor: "grey.300",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ pb: 1 }}>
              {t("reviewerActionOverview")}
            </Typography>
            <CombinedReviewerActionsChart
              tenants={activeTenantDescriptors}
              start={start}
              end={end}
            />
          </Box>
        </Stack>
      )}

      <Stack spacing={2}>
        {activeTenantDescriptors.map((tenant) => (
          <TenantReviewerCard
            key={tenant.tenantName}
            tenantName={tenant.tenantName}
            tenantLongName={tenant.tenantLongName}
            start={start}
            end={end}
          />
        ))}
      </Stack>

      {scrapEyeTenants.length === 0 && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="text.secondary">{t("noTenantsFound")}</Typography>
        </Box>
      )}
    </Stack>
  );
};

export const ReviewerTrackingOverviewPage = () => (
  <DateRangeProvider config={REVIEWER_TRACKING_DATE_RANGE_CONFIG}>
    <ReviewerTrackingOverviewContent />
  </DateRangeProvider>
);
